import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useWheelSpin } from '@/hooks/useWheelSpin';
import { useGems } from '@/hooks/useGems';
import { useBoosts } from '@/hooks/useBoosts';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { WheelPrizeModal } from './WheelPrizeModal';
import { WheelCountdown } from './WheelCountdown';
import { Gem, Sparkles, Loader2, Shield } from 'lucide-react';
import type { WheelPrize } from '@/types/gems';

const WHEEL_SIZE = 320;
const SPIN_DURATION = 5;
const EXTRA_ROTATIONS = 5;

export const WheelOfFortune = () => {
  const { spinsInfo, prizes, isSpinning, loading, hasFreeSpins, spin, fetchSpinsInfo, fetchPrizes } = useWheelSpin();
  const { refreshGems, balance: gemBalance } = useGems();
  const { refreshBoosts } = useBoosts();
  const { isAdmin } = useIsAdmin();
  const controls = useAnimation();
  const [currentRotation, setCurrentRotation] = useState(0);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [wonPrize, setWonPrize] = useState<WheelPrize | null>(null);
  const [adminTestMode, setAdminTestMode] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchSpinsInfo();
    fetchPrizes();
  }, [fetchSpinsInfo, fetchPrizes]);

  const handleCountdownExpired = useCallback(() => {
    fetchSpinsInfo();
  }, [fetchSpinsInfo]);

  const handleSpin = async (useGems: boolean = false) => {
    if (isSpinning) return;

    const result = await spin(useGems, adminTestMode);
    
    if (result?.success && result.prize) {
      // Encontrar o índice REAL do prêmio no array prizes (por slot_number)
      const prizeIndex = prizes.findIndex(p => p.slot_number === result.prize!.slot);
      
      if (prizeIndex === -1) {
        console.error('Prêmio não encontrado no array de prizes:', result.prize.slot);
        return;
      }

      // Calcular ângulo por slot
      const slotAngle = 360 / prizes.length;
      
      // Calcular onde o CENTRO do slot premiado está na roleta
      // Slot no índice i está centrado em (i * slotAngle + slotAngle/2)
      const prizeSlotCenterAngle = prizeIndex * slotAngle + slotAngle / 2;
      
      // O ponteiro está no TOPO (0°/360°)
      // Para alinhar o centro do slot com o ponteiro, rotação = 360 - prizeSlotCenterAngle
      const targetAngle = 360 - prizeSlotCenterAngle;
      
      // Normalizar base para evitar drift acumulativo
      const baseRotation = Math.ceil(currentRotation / 360) * 360;
      const totalRotation = baseRotation + (EXTRA_ROTATIONS * 360) + targetAngle;

      // Animar a roleta
      await controls.start({
        rotate: totalRotation,
        transition: {
          duration: SPIN_DURATION,
          ease: [0.2, 0.8, 0.3, 1]
        }
      });

      setCurrentRotation(totalRotation);
      setWonPrize(result.prize);
      setShowPrizeModal(true);

      // Atualizar saldos
      if (result.prize.type === 'gems' || result.prize.type === 'nothing') {
        refreshGems();
      }
      if (result.prize.type === 'boost') {
        refreshBoosts();
      }
      if (result.prize.type === 'vip_day') {
        refreshGems();
        refreshBoosts();
      }
    }
  };

  const closePrizeModal = () => {
    setShowPrizeModal(false);
    setWonPrize(null);
    fetchSpinsInfo();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-luxury-pinkBright" />
      </div>
    );
  }

  const slotAngle = 360 / prizes.length;

  const showCountdown = spinsInfo && spinsInfo.free_spins_remaining === 0 && spinsInfo.next_reset_at;

  return (
    <div className="flex flex-col items-center gap-6 p-4">
      {/* Toggle Modo Admin */}
      {isAdmin && (
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2">
          <Shield className="w-4 h-4 text-amber-400" />
          <Label htmlFor="admin-mode" className="text-amber-300 text-sm font-medium cursor-pointer">
            Modo Admin (giros ilimitados)
          </Label>
          <Switch 
            id="admin-mode"
            checked={adminTestMode} 
            onCheckedChange={setAdminTestMode}
            className="data-[state=checked]:bg-amber-500"
          />
        </div>
      )}

      {/* Header com saldo */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
          <Gem className="w-4 h-4 text-purple-400" />
          <span className="text-white font-medium">{gemBalance}</span>
          <span className="text-white/60">gemas</span>
        </div>
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
          <Sparkles className="w-4 h-4 text-luxury-pinkBright" />
          <span className="text-white font-medium">{spinsInfo?.free_spins_remaining || 0}</span>
          <span className="text-white/60">giros grátis</span>
        </div>
      </div>

      {/* Countdown para próximo giro grátis */}
      {showCountdown && (
        <WheelCountdown 
          nextResetAt={spinsInfo.next_reset_at} 
          onExpired={handleCountdownExpired}
        />
      )}

      {/* Wheel Container */}
      <div className="relative" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}>
        {/* Pointer/Indicator */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
          <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-luxury-pinkBright drop-shadow-lg" />
        </div>

        {/* Wheel */}
        <motion.div
          animate={controls}
          className="relative w-full h-full rounded-full overflow-hidden shadow-2xl"
          style={{
            background: 'conic-gradient(from 0deg, ' + 
              prizes.map((p, i) => `${p.display_color} ${i * slotAngle}deg ${(i + 1) * slotAngle}deg`).join(', ') + ')'
          }}
        >
          {/* Slot labels */}
          {prizes.map((prize, index) => {
            const rotation = index * slotAngle + slotAngle / 2;
            return (
              <div
                key={prize.id}
                className="absolute w-full h-full flex items-center justify-center"
                style={{
                  transform: `rotate(${rotation}deg)`
                }}
              >
                <div 
                  className="absolute flex flex-col items-center gap-0.5"
                  style={{ 
                    transform: `translateY(-${WHEEL_SIZE / 3}px) rotate(0deg)`,
                  }}
                >
                  <span className="text-2xl">{prize.display_emoji}</span>
                  <span className="text-[10px] font-bold text-white drop-shadow-md whitespace-nowrap">
                    {prize.display_label}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Center circle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-4 border-white/20 shadow-inner flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-luxury-pinkBright" />
          </div>
        </motion.div>

        {/* Glow effect */}
        <div className="absolute inset-0 rounded-full pointer-events-none" 
          style={{ boxShadow: '0 0 60px rgba(233, 30, 99, 0.3), inset 0 0 30px rgba(0,0,0,0.3)' }} 
        />
      </div>

      {/* Spin Buttons */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {hasFreeSpins ? (
          <Button
            onClick={() => handleSpin(false)}
            disabled={isSpinning}
            variant="brand-gradient"
            className="w-full font-semibold py-6 text-lg"
          >
            {isSpinning ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Sparkles className="w-5 h-5 mr-2" />
            )}
            Girar Grátis ({spinsInfo?.free_spins_remaining})
          </Button>
        ) : (
          <Button
            onClick={() => handleSpin(true)}
            disabled={isSpinning || !spinsInfo?.can_buy_spin}
            variant="gem"
            className="w-full font-semibold py-6 text-lg"
          >
            {isSpinning ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Gem className="w-5 h-5 mr-2" />
            )}
            Girar com 1 Gema
          </Button>
        )}

        <p className="text-center text-xs text-white/40">
          {spinsInfo?.max_free_spins === 1 && 'Usuários pagos têm mais giros grátis!'}
          {spinsInfo?.max_free_spins === 2 && 'Upgrade para Infinity e ganhe 3 giros diários!'}
          {spinsInfo?.max_free_spins === 3 && 'Você tem o máximo de giros gratuitos! 🎉'}
        </p>
      </div>

      {/* Prize Modal */}
      <WheelPrizeModal 
        isOpen={showPrizeModal} 
        onClose={closePrizeModal} 
        prize={wonPrize} 
      />
    </div>
  );
};
